/**
 * The Data guide: every line item once, with what it means, its unit and
 * where its value comes from. The same dictionary the Analyst reads before it
 * answers, so a person and the model mean the same thing by "net income".
 */

import React from "react";
import type { PortfolioLineItem } from "../../services/portfolioDataService";
import {
	ORIGIN_LABEL,
	ORIGIN_OF_BASIS,
	ORIGIN_PILL,
	inWords,
	lineItemLabel,
} from "./portfolioModel";

/** What the grid does with a line item, where it differs from the workbook's. */
const ON_THE_PAGE: Record<string, string> = {
	vacancy_loss: "The tables show it as a share of rent billed, which the page works out.",
};

export interface DataGuideProps {
	items: readonly PortfolioLineItem[];
}

const SCOPES: { id: PortfolioLineItem["scope"]; label: string }[] = [
	{ id: "house", label: "Per house" },
	{ id: "plat", label: "Per plat" },
];

const DataGuide: React.FC<DataGuideProps> = ({ items }) => (
	<div className="flex flex-col gap-5">
		{SCOPES.map((scope) => {
			const rows = items.filter((item) => item.scope === scope.id);
			if (rows.length === 0) return null;
			return (
				<section key={scope.id} className="flex flex-col gap-2">
					<h2 className="text-xs font-semibold uppercase tracking-[0.06em] text-[#6B7280]">
						{scope.label} · {rows.length} line items
					</h2>
					<div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
						<table className="w-full border-collapse text-[13px]">
							<thead>
								<tr className="text-left">
									<th scope="col" className="border-b border-gray-200 bg-[#fafafa] px-3 py-2 text-[12.5px] font-semibold text-[#1F2937]">
										Line item
									</th>
									<th scope="col" className="border-b border-gray-200 bg-[#fafafa] px-3 py-2 text-[12.5px] font-semibold text-[#1F2937]">
										Where it comes from
									</th>
									<th scope="col" className="border-b border-gray-200 bg-[#fafafa] px-3 py-2 text-[12.5px] font-semibold text-[#1F2937]">
										What it means
									</th>
								</tr>
							</thead>
							<tbody>
								{rows.map((item) => {
									const origin = ORIGIN_OF_BASIS[item.basis];
									return (
										<tr key={item.line_item} className="align-top">
											<td className="whitespace-nowrap border-b border-[#f1f2f4] px-3 py-2.5">
												<span className="text-[13px] font-medium text-[#111111]">{lineItemLabel(item.line_item)}</span>
												<div className="font-mono text-[11px] text-[#9a9ea3]">
													{item.line_item} · {item.unit === "ratio" ? "ratio" : "$"}
												</div>
											</td>
											<td className="whitespace-nowrap border-b border-[#f1f2f4] px-3 py-2.5">
												<span
													className={`inline-flex h-[22px] items-center rounded-full px-2 text-[11.5px] font-semibold ${ORIGIN_PILL[origin]}`}
												>
													{ORIGIN_LABEL[origin]}
												</span>
											</td>
											<td className="border-b border-[#f1f2f4] px-3 py-2.5 leading-relaxed text-[#1F2937]">
												{inWords(item.definition)}
												{ON_THE_PAGE[item.line_item] && (
													<span className="block text-[12.5px] text-[#6B7280]">{ON_THE_PAGE[item.line_item]}</span>
												)}
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				</section>
			);
		})}
	</div>
);

export default DataGuide;
